import React, { useState, useCallback, useEffect } from 'react';
import { Button, CardComponent, AlertComponent, InputComponent } from '../../../components/shared';
import { authActions, MFAChallengeData } from '../../../state/features/authReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state/features/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

export interface MFAEmailChallengeProps {
    onComplete: () => void;
    onCancel: () => void;
    mfaChallenge: MFAChallengeData;
}

export const MFAEmailChallenge: React.FC<MFAEmailChallengeProps> = ({ 
    onComplete, 
    onCancel, 
    mfaChallenge 
}) => {
    const [emailCode, setEmailCode] = useState<string>('');
    const [resendCooldown, setResendCooldown] = useState<number>(0);
    
    const { t } = useTranslation();
    const errorMessage = useSelector((x: RootState) => x.auth.error);
    const authLoading = useSelector((x: RootState) => x?.auth?.loading);
    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

    const onSubmit = useCallback(async () => {
        if (emailCode.length >= 6) {
            dispatch(authActions.respondToEmailMFA({
                username: mfaChallenge.username,
                sessionToken: mfaChallenge.Session,
                emailCode: emailCode.trim()
            }));
        }
    }, [emailCode, mfaChallenge, dispatch]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            onSubmit();
        }
    }, [onSubmit]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    // Cooldown timer for resend functionality
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleResendCode = () => {
        if (resendCooldown === 0) {
            //dispatch(authActions.res({ username: mfaChallenge.username }));
            setResendCooldown(60); // 60 second cooldown
        }
    };

    const clickGate = Boolean(authLoading);

    return (
        <CardComponent title={t('mfa.title')} className='login-card'>
            {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}
            
            <div style={{ marginBottom: '20px', textAlign: 'center', color: 'white' }}>
                <p>{t('mfa.verificationCodeSent')}</p>
                <p>{t('mfa.enterSixDigitCode')}</p>
            </div>

            <div className='fieldView'>
                <label className='labelView'>{t('auth.verificationCode')}</label>
                <InputComponent
                    value={emailCode}
                    placeholder={t('auth.enterCodeSentToEmail')}
                    onChangeEvent={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                        if (value.length <= 6) {
                            setEmailCode(value);
                        }
                    }}
                    disabled={clickGate}
                    maxLength={6}
                />
            </div>

            <div className='signBtnView'>
                <Button
                    type='primary'
                    onClickEvent={onSubmit}
                    disabled={clickGate || emailCode.length < 6}
                    className='SignBtn'
                >
                    {t('auth.verifyCode')}
                </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <Button
                    type='link'
                    onClickEvent={handleResendCode}
                    disabled={resendCooldown > 0 || clickGate}
                    className='forgetPassword'
                >
                    {resendCooldown > 0 
                        ? `${t('mfa.resendCodeIn')} ${resendCooldown}s` 
                        : t('mfa.resendCode')
                    }
                </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <Button
                    type='link'
                    onClickEvent={onCancel}
                    className='forgetPassword'
                >
                    {t('mfa.cancel')}
                </Button>
            </div>
        </CardComponent>
    );
};
